import { useState } from 'react';
import { Alert, AlertTitle, Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';

import { conciergeApi } from '../../services/concierge-api-client';
import { placeLookupMessage, type PlaceLookupResult } from '../../lib/placeLookup';
import type { ResolvedPlace } from '../../types/groups';

export type { ResolvedPlace };

export interface PlacePickerProps {
  groupId: string;
  /** The trip's location, so "Time Out Market" finds the Lisbon one. */
  near?: string | null;
  /** Already-chosen place, shown as confirmed rather than as a search box. */
  value: ResolvedPlace | null;
  onChange: (place: ResolvedPlace | null) => void;
  disabled?: boolean;
  /**
   * The location text no longer matches the place this pin came from, and
   * nobody has confirmed the pin during this edit. Shown as a warning with two
   * ways out, because the alternative — saying nothing — is how a tile ends up
   * naming one venue and pinning another.
   */
  stale?: boolean;
  /** "Keep this pin": the host has read the warning and stands by the place. */
  onKeep?: () => void;
}

/**
 * Name a venue instead of typing its coordinates.
 *
 * The fields behind this are latitude and longitude, and asking a host for those
 * is asking the wrong person for the wrong thing — nobody planning a birthday
 * knows a decimal degree, and the map is empty until somebody does. So the host
 * names the place, the lookup returns the identity and the facts, and the
 * coordinates arrive as a side effect of a question a person can answer.
 *
 * The result is a proposal, not a commitment: it is shown in full, nothing
 * persists until the form around this is saved, and a lookup that finds the wrong
 * Time Out Market is a visible mistake rather than a silent one.
 */
export default function PlacePicker({
  groupId,
  near,
  value,
  onChange,
  disabled,
  stale,
  onKeep,
}: PlacePickerProps) {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [notice, setNotice] = useState<{ text: string; severity: 'info' | 'warning' } | null>(null);
  /*
    "Change" opens the search without discarding what is there.

    Clearing on Change put the form in a state it could not save: the API
    COALESCEs the place fields, so a save with none sent keeps the stored one —
    the host would see an empty picker, save, and find the old venue still
    attached. Keeping the current value until a new one replaces it means the
    screen and the database never disagree.
  */
  const [replacing, setReplacing] = useState(false);

  const report = (result: PlaceLookupResult, trimmed: string) => {
    const text = placeLookupMessage(result, trimmed);
    if (!text) {
      setNotice(null);
      return;
    }
    // An outage is a warning, not a footnote: it is the state in which a host
    // gives up on the lookup and types the venue by hand.
    setNotice({ text, severity: result.status === 'unavailable' ? 'warning' : 'info' });
  };

  const search = async () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    setSearching(true);
    setNotice(null);
    try {
      const result = await conciergeApi.resolveGroupPlace(groupId, {
        query: trimmed,
        city: near || undefined,
      });
      report(result, trimmed);
      if (result.status !== 'found') return;
      onChange(result.place);
      setQuery('');
      setReplacing(false);
    } finally {
      setSearching(false);
    }
  };

  const staleWarning = stale ? (
    <Alert
      severity="warning"
      sx={{ mb: 1.5 }}
      action={
        onKeep ? (
          <Button size="small" color="inherit" disabled={disabled} onClick={onKeep}>
            Keep this pin
          </Button>
        ) : undefined
      }
    >
      <AlertTitle>This pin is from before you changed the location</AlertTitle>
      It still points at <strong>{value?.name || 'the previous place'}</strong>
      {value?.address ? ` (${value.address})` : ''}. Find the new place below, or keep this pin on
      purpose — otherwise it is removed when you save, because a guest can navigate to a wrong pin.
    </Alert>
  ) : null;

  const noticeAlert = notice ? (
    <Alert severity={notice.severity} sx={{ mt: 1.5 }} onClose={() => setNotice(null)}>
      {notice.text}
    </Alert>
  ) : null;

  if (value && !replacing && !stale) {
    return (
      <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box sx={{ minWidth: 0 }}>
            <Typography fontWeight={600}>{value.name}</Typography>
            {value.address ? (
              <Typography variant="body2" color="text.secondary">
                {value.address}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
              {value.tripsbyLocationId ? (
                <Chip size="small" label="Matched in Tripsby" color="success" variant="outlined" />
              ) : (
                // Worth saying: without it the venue is a name and an address,
                // not something the concierge can answer questions about.
                <Chip size="small" label="Not in Tripsby yet" variant="outlined" />
              )}
              {value.latitude != null && value.longitude != null ? (
                <Chip size="small" label="On the map" variant="outlined" />
              ) : (
                <Chip size="small" label="No map pin" color="warning" variant="outlined" />
              )}
            </Stack>
          </Box>
          <Button size="small" disabled={disabled} onClick={() => setReplacing(true)}>
            Change
          </Button>
        </Stack>
        {/*
          The notice belongs here too, not only over the search box.

          A successful lookup flips this component into the confirmed view in the
          same tick it reports "found it, but we couldn't load its address or map
          pin" — so the one message that is only ever raised ON a success was set
          into state and then rendered by the branch the component had just left.
          The host saw a confirmed place with no mention that its address and pin
          are missing, which is the case they most need to check before saving.
        */}
        {noticeAlert}
      </Box>
    );
  }

  return (
    <Box>
      {staleWarning}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
        <TextField
          fullWidth
          size="small"
          label="Find a place"
          placeholder={near ? `e.g. a restaurant in ${near}` : 'Search for a venue or hotel'}
          value={query}
          disabled={disabled || searching}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            // Enter searches rather than submitting the form around it, which
            // would save an item the host has not finished describing.
            if (event.key === 'Enter') {
              event.preventDefault();
              void search();
            }
          }}
          helperText="Fills in the address and puts it on the guest map."
        />
        <Button
          variant="outlined"
          disabled={disabled || searching || !query.trim()}
          onClick={() => void search()}
          sx={{ mt: { sm: 0.5 }, flexShrink: 0 }}
        >
          {searching ? 'Looking…' : 'Find'}
        </Button>
        {value && !stale ? (
          <Button
            size="small"
            disabled={disabled || searching}
            onClick={() => {
              setReplacing(false);
              setQuery('');
              setNotice(null);
            }}
            sx={{ mt: { sm: 0.5 }, flexShrink: 0 }}
          >
            Keep {value.name}
          </Button>
        ) : null}
      </Stack>
      {noticeAlert}
    </Box>
  );
}
